import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Gift,
  Wallet
} from 'lucide-react'
import { dashboardService, AssetFlowData } from '@/services/dashboardService'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

type PeriodType = 'today' | 'month' | 'custom'

const AssetFlow = () => {
  const { t } = useTranslation(['dashboard', 'common'])
  // 이번 주 월요일과 오늘 날짜 계산
  const getThisWeekDates = () => {
    const today = new Date()
    const day = today.getDay()
    const diff = today.getDate() - day + (day === 0 ? -6 : 1) // 월요일로 조정
    const monday = new Date(today.setDate(diff))
    const todayDate = new Date()

    return {
      start: monday.toISOString().split('T')[0],
      end: todayDate.toISOString().split('T')[0]
    }
  }

  const thisWeek = getThisWeekDates()

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<AssetFlowData | null>(null)
  const [period, setPeriod] = useState<PeriodType>('today')
  const [startDate, setStartDate] = useState(thisWeek.start)
  const [endDate, setEndDate] = useState(thisWeek.end)

  const loadData = async () => {
    try {
      setLoading(true)
      const result = await dashboardService.getAssetFlow(period, startDate, endDate)
      setData(result)
    } catch (error) {
      console.error('자산 흐름 로딩 실패:', error)
      toast.error(t('dashboard:assetFlow.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handlePeriodChange = (newPeriod: PeriodType) => {
    setPeriod(newPeriod)

    // custom 선택 시 기본값으로 이번 주 설정
    if (newPeriod === 'custom') {
      const week = getThisWeekDates()
      setStartDate(week.start)
      setEndDate(week.end)
    }
  }

  useEffect(() => {
    loadData()
  }, [period])

  const handleCustomSearch = () => {
    if (!startDate || !endDate) {
      toast.error(t('dashboard:assetFlow.selectDatesError'))
      return
    }
    loadData()
  }

  if (loading) {
    return (
      <div className="glass-dark p-6">
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-8 h-8"></div>
        </div>
      </div>
    )
  }

  if (!data) return null

  const stats = [
    {
      title: t('assetFlow.totalHoldings'),
      value: (
        <span>
          <span className="text-blue-400">${data.total_balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          <span className="text-dark-400 text-sm"> / </span>
          <span className="text-yellow-400">${data.total_bonus.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          <br />
          <span className="text-sky-400 text-base">{Math.round(data.total_balance_krw || 0).toLocaleString()}원</span>
          <span className="text-dark-400 text-sm"> / </span>
          <span className="text-amber-300 text-base">{Math.round(data.total_bonus_krw || 0).toLocaleString()}원</span>
        </span>
      ),
      icon: Wallet,
      color: 'text-primary-400',
      bgColor: 'bg-primary-500/20'
    },
    {
      title: t('assetFlow.todayDeposits'),
      value: (
        <span>
          <span className="text-green-400">${data.total_deposits.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          <br />
          <span className="text-green-400 text-base">{t('header:Header.k_c404cb', { val0: Math.round(data.total_deposits_krw || 0).toLocaleString() })}</span>
        </span>
      ),
      icon: TrendingUp,
      color: 'text-green-400',
      bgColor: 'bg-green-500/20'
    },
    {
      title: t('assetFlow.todayWithdrawals'),
      value: (
        <span>
          <span className="text-red-400">${data.total_withdrawals.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          <br />
          <span className="text-red-400 text-base">{t('header:Header.k_c404cb', { val0: Math.round(data.total_withdrawals_krw || 0).toLocaleString() })}</span>
        </span>
      ),
      icon: TrendingDown,
      color: 'text-red-400',
      bgColor: 'bg-red-500/20'
    },
    {
      title: t('assetFlow.todayGrants'),
      value: (() => {
        const money = data.total_granted_money || 0
        const bonus = data.total_granted_bonus || 0
        const moneySign = money >= 0 ? '+' : '-'
        const bonusSign = bonus >= 0 ? '+' : '-'
        const moneyColor = money >= 0 ? 'text-blue-400' : 'text-red-400'
        const bonusColor = bonus >= 0 ? 'text-yellow-400' : 'text-red-400'
        
        const moneyKrw = data.total_granted_money_krw || 0
        const bonusKrw = data.total_granted_bonus_krw || 0
        return (
          <span>
            <span className={moneyColor}>{moneySign}${Math.abs(money).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            <span className="text-dark-400 text-sm"> / </span>
            <span className={bonusColor}>{bonusSign}${Math.abs(bonus).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            <br />
            <span className={`text-base ${moneyKrw >= 0 ? 'text-sky-400' : 'text-red-400'}`}>{moneyKrw >= 0 ? '+' : '-'}{Math.abs(Math.round(moneyKrw)).toLocaleString()}원</span>
            <span className="text-dark-400 text-sm"> / </span>
            <span className={`text-base ${bonusKrw >= 0 ? 'text-amber-300' : 'text-red-400'}`}>{bonusKrw >= 0 ? '+' : '-'}{Math.abs(Math.round(bonusKrw)).toLocaleString()}원</span>
          </span>
        )
      })(),
      icon: Gift,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/20'
    }
  ]

  return (
    <div className="glass-dark p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center space-x-2">
            <DollarSign className="w-5 h-5 sm:w-6 sm:h-6" />
            <span>{t('dashboard:assetFlow.flowTitle')}</span>
          </h2>
          <p className="text-sm text-dark-400 mt-1">
            {data.start_date} ~ {data.end_date}
          </p>
        </div>

        {/* Period Filter */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => handlePeriodChange('today')}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              period === 'today'
                ? 'bg-primary-500 text-white'
                : 'glass-button'
            }`}
          >{t('dashboard:assetFlow.today')}</button>
          <button
            onClick={() => handlePeriodChange('month')}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              period === 'month'
                ? 'bg-primary-500 text-white'
                : 'glass-button'
            }`}
          >{t('dashboard:assetFlow.thisMonth')}</button>
          <button
            onClick={() => handlePeriodChange('custom')}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              period === 'custom'
                ? 'bg-primary-500 text-white'
                : 'glass-button'
            }`}
          >{t('dashboard:assetFlow.customPeriod')}</button>
        </div>
      </div>

      {/* Custom Date Range */}
      {period === 'custom' && (
        <div className="mb-6 p-4 glass-darker rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <label className="text-sm text-dark-300 mb-1 block">{t('dashboard:assetFlow.startDate')}</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input-glass w-full"
              />
            </div>
            <div className="flex-1">
              <label className="text-sm text-dark-300 mb-1 block">{t('dashboard:assetFlow.endDate')}</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input-glass w-full"
              />
            </div>
            <button
              onClick={handleCustomSearch}
              className="btn-primary whitespace-nowrap"
            >{t('dashboard:assetFlow.search')}</button>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="glass-darker p-4 rounded-lg"
          >
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
            </div>
            <p className="text-sm text-dark-400 mb-1">{stat.title}</p>
            <div className={`text-xl font-bold ${typeof stat.value === 'string' ? stat.color : ''}`}>
              {stat.value}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Net Flow Summary */}
      <div className={`p-4 rounded-lg ${
        data.net_flow >= 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
      }`}>
        <div className="flex items-center justify-between">
          <span className="text-dark-300">{t('dashboard:assetFlow.netAssetChange')}</span>
          <span className={`text-xl font-bold ${data.net_flow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {data.net_flow >= 0 ? '+' : ''}${data.net_flow.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>
    </div>
  )
}

export default AssetFlow
